import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Star, ShieldCheck, Microscope, HeartPulse, Award, ArrowRight, MapPin, Clock, MessageCircle, CheckCircle2 } from 'lucide-react';
import { CLINIC, IMAGES, DISORDERS, DOCTORS, RECOGNITION } from '../mock';
import { VideoTestimonial } from '../components/VideoTestimonial';
import { AwardCard, RadioFeature, PressFeature } from '../components/Recognition';
import { Reveal, SectionTitle } from '../components/Shared';
import { TrustStrip } from '../components/TrustStrip';
import { SymptomChecker } from '../components/SymptomChecker';
import { FirstVisit } from '../components/FirstVisit';
import { OnlineConsult } from '../components/OnlineConsult';
import { ReviewsWall } from '../components/ReviewsWall';
import { useLang } from '../context/LangContext';

const CREDS = ['MRCP (UK)', 'MRCP Rheumatology (UK)', 'MSK Ultrasound, Cambridge', 'EULAR'];
const SYMPTOMS = [
  { id: 'stiffness', label: 'Morning stiffness', hi: 'सुबह की अकड़न', to: '#symptom-checker' },
  { id: 'swelling', label: 'Swollen joints', hi: 'जोड़ों में सूजन', to: '#symptom-checker' },
  { id: 'back-pain', label: 'Back pain at night', hi: 'रात में कमर दर्द', to: '#symptom-checker' },
  { id: 'ana', label: 'Positive ANA', hi: 'ANA पॉज़िटिव', to: '/patient-education/positive-ana-meaning' },
  { id: 'hla-b27', label: 'HLA-B27 positive', hi: 'HLA-B27 पॉज़िटिव', to: '/patient-education/hla-b27-positive' },
  { id: 'second-opinion', label: 'Need a second opinion', hi: 'सेकंड ओपिनियन चाहिए', to: '/second-opinion' },
];

const FEATURES = [
  { icon: Award, hiTitle: 'यूके कंसल्टेंट विशेषज्ञता', hiBody: 'नोएडा व दिल्ली NCR में यूके कंसल्टेंट-स्तर के प्रशिक्षण और NHS अनुभव वाले एकमात्र रूमेटोलॉजिस्ट।', title: 'UK Consultant Expertise', body: 'The only rheumatologist in Noida & Delhi NCR with UK Consultant-level training and NHS experience.' },
  { icon: Microscope, hiTitle: 'सटीक निदान', hiBody: 'सही और जल्दी निदान के लिए आधुनिक ब्लड टेस्ट, इमेजिंग और मस्कुलोस्केलेटल अल्ट्रासाउंड।', title: 'Precise Diagnosis', body: 'Advanced serology, imaging and musculoskeletal ultrasound for accurate, early diagnosis.' },
  { icon: HeartPulse, hiTitle: 'व्यक्तिगत इलाज', hiBody: 'DMARDs और आधुनिक बायोलॉजिक थेरेपी सहित, हर मरीज़ के लिए प्रमाण-आधारित इलाज योजना।', title: 'Personalised Treatment', body: 'Evidence-based, individualised plans including DMARDs and modern biologic therapies.' },
  { icon: ShieldCheck, hiTitle: 'संवेदनशील देखभाल', hiBody: 'एक शांत, सहयोगी माहौल जहाँ आपकी लंबी अवधि की बेहतर ज़िंदगी पर ध्यान दिया जाता है।', title: 'Compassionate Care', body: 'A calm, supportive environment focused on your long-term quality of life.' },
];

export default function Home() {
  const { tr } = useLang();
  return (
    <div>
      {/* HERO */}
      <section data-testid="home-hero" className="hero-gradient relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full blob" style={{ background: 'rgba(20,184,166,0.12)' }} />
        <div className="container-x pt-10 pb-16 md:py-20 relative z-10 grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-teal-700">
              <span className="w-8 h-[2px]" style={{ background: 'var(--coral)' }} /> {tr('UK-trained Consultant Rheumatologist', 'यूके प्रशिक्षित कंसल्टेंट रूमेटोलॉजिस्ट')}
            </span>
            <h1 data-testid="home-hero-title" className="text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.04] text-teal-900 text-balance mt-5" style={{ fontWeight: 600 }}>
              {tr(<>Joint pain, stiffness or swelling? <span style={{ color: 'var(--coral)' }}>Get answers</span>, not just painkillers.</>, <>जोड़ों में दर्द, अकड़न या सूजन? सिर्फ़ दर्द की दवा नहीं, <span style={{ color: 'var(--coral)' }}>सही जवाब</span> पाएँ।</>)}
            </h1>
            <p className="text-slate-600 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
              {tr('Expert diagnosis and modern treatment for arthritis, lupus, spondylitis and autoimmune disease, from Dr Puneet Srivastava in Noida, Ghaziabad, Agra, Moradabad & Aligarh.', 'आर्थराइटिस, ल्यूपस, स्पॉन्डिलाइटिस और ऑटोइम्यून रोगों का विशेषज्ञ निदान और आधुनिक इलाज: डॉ. पुनीत श्रीवास्तव, नोएडा, गाज़ियाबाद, आगरा, मुरादाबाद और अलीगढ़ में।')}
            </p>
            <div className="mt-7">
              <div className="text-sm font-medium text-slate-500 mb-3">{tr('What brings you here?', 'आपकी समस्या क्या है?')}</div>
              <div className="flex flex-wrap gap-2">
                {SYMPTOMS.map((s) => (
                  s.to.startsWith('#')
                    ? <a key={s.label} data-testid={`hero-symptom-${s.id}`} href={s.to} className="symptom-chip">{tr(s.label, s.hi)}</a>
                    : <Link key={s.label} data-testid={`hero-symptom-${s.id}`} to={s.to} className="symptom-chip">{tr(s.label, s.hi)}</Link>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <a data-testid="hero-call-button" href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold">
                <Phone size={18} /> {tr('Book', 'बुक करें')}: {CLINIC.phone}
              </a>
              <a data-testid="hero-checker-button" href="#symptom-checker" className="btn-outline inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-white/60">
                {tr('Check your symptoms', 'अपने लक्षण जाँचें')} <ArrowRight size={18} />
              </a>
            </div>
          </div>

          <div className="relative max-w-md w-full mx-auto mt-4 lg:mt-0">
            <div className="relative rounded-full p-2 shadow-2xl" style={{ background: 'linear-gradient(140deg,#ffffff,#ccf5e8)' }}>
              <span className="absolute -inset-4 rounded-full border-2 border-dashed border-teal-200 animate-[spin_60s_linear_infinite]" />
              <img data-testid="home-hero-doctor-photo" src="/dr-puneet-hero.webp" alt="Dr Puneet Srivastava, Consultant Rheumatologist at Srigyan Rheumatology Clinic" className="relative w-full aspect-square rounded-full object-cover" />
            </div>
            <div className="absolute left-1/2 -translate-x-1/2 -bottom-10 w-[88%] bg-white rounded-2xl shadow-xl px-5 py-3.5">
              <div className="font-display text-lg text-teal-900" style={{ fontWeight: 600 }}>{tr('Dr Puneet Srivastava', 'डॉ. पुनीत श्रीवास्तव')}</div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {CREDS.map((c) => <span key={c} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-teal-50 text-teal-800">{c}</span>)}
              </div>
            </div>
            <a data-testid="hero-rating-card" href={CLINIC.mapCid} target="_blank" rel="noreferrer" className="absolute -left-3 sm:-left-8 top-10 bg-white rounded-2xl shadow-xl px-4 py-3 floaty">
              <div className="flex items-center gap-1 font-display text-xl text-teal-900" style={{ fontWeight: 700 }}>{CLINIC.rating}<Star size={16} className="fill-amber-400 text-amber-400" /></div>
              <div className="text-[11px] text-slate-500">{CLINIC.reviews} {tr('Google reviews', 'गूगल रिव्यू')}</div>
            </a>
            <a data-testid="hero-threebestrated-badge" href={RECOGNITION.url} target="_blank" rel="noreferrer" className="absolute -right-3 sm:-right-8 top-1/2 bg-teal-900 text-white rounded-2xl shadow-xl px-4 py-3 max-w-[150px] hover:bg-teal-800 transition-colors">
              <Award size={18} className="text-amber-300" />
              <div className="text-xs font-semibold mt-1 leading-snug">{tr('Top 3 Rheumatologist in Ghaziabad', 'गाज़ियाबाद के टॉप 3 रूमेटोलॉजिस्ट')}</div>
              <div className="text-[10px] text-teal-200 mt-0.5">ThreeBestRated</div>
            </a>
          </div>
        </div>
      </section>

      <TrustStrip />

      <ReviewsWall />

      {/* SYMPTOM CHECKER */}
      <section id="symptom-checker" data-testid="home-symptom-checker-section" className="py-20 scroll-mt-24">
        <div className="container-x">
          <SectionTitle center eyebrow={tr('60-second check', '60 सेकंड की जाँच')} title={tr('Should I see a rheumatologist?', 'क्या मुझे रूमेटोलॉजिस्ट को दिखाना चाहिए?')} sub={tr('Answer 6 quick yes/no questions about your symptoms.', 'अपने लक्षणों के बारे में 6 आसान हाँ/ना सवालों के जवाब दें।')} />
          <SymptomChecker />
        </div>
      </section>

      {/* RECOGNITION & MEDIA */}
      <section className="pt-20" data-testid="home-recognition-section">
        <div className="container-x">
          <SectionTitle center eyebrow={tr('Recognition & Media', 'सम्मान और मीडिया')} title={tr('Trusted across Ghaziabad & Noida', 'गाज़ियाबाद और नोएडा का भरोसा')} />
          <div className="grid lg:grid-cols-2 gap-6 lg:items-start">
            <Reveal><AwardCard /></Reveal>
            <Reveal delay={90}><RadioFeature idPrefix="home-radio-feature" /></Reveal>
          </div>
          <Reveal><div className="mt-6"><PressFeature idPrefix="home-press-feature" /></div></Reveal>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20">
        <div className="container-x">
          <SectionTitle center eyebrow={tr('Why Srigyan Clinic', 'श्रीज्ञान क्लिनिक क्यों')} title={tr('World-class rheumatology, close to home', 'विश्व-स्तरीय रूमेटोलॉजी, आपके घर के पास')} sub={tr('We combine international expertise with warm, personalised care to help you manage your condition and reclaim your quality of life.', 'हम अंतरराष्ट्रीय विशेषज्ञता को आत्मीय, व्यक्तिगत देखभाल के साथ जोड़ते हैं, ताकि आप अपनी बीमारी को संभाल सकें और बेहतर जीवन जी सकें।')} />
          <div className="grid md:grid-cols-4 sm:grid-cols-2 gap-6">
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={i * 90}>
                <div className="card-lift bg-white border border-teal-50 rounded-2xl p-7 h-full">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl text-white mb-5" style={{ background: 'linear-gradient(135deg,#0f766e,#0b3b3a)' }}>
                    <f.icon size={26} />
                  </span>
                  <h3 className="text-lg text-teal-900 mb-2" style={{ fontWeight: 600 }}>{tr(f.title, f.hiTitle)}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{tr(f.body, f.hiBody)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DISORDERS PREVIEW */}
      <section className="mint-section py-20">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <SectionTitle eyebrow={tr('Conditions', 'बीमारियाँ')} title={tr('Disorders we treat', 'रोग जिनका हम इलाज करते हैं')} sub={tr('From common arthritis to rare autoimmune syndromes — comprehensive care under one roof.', 'आम आर्थराइटिस से लेकर दुर्लभ ऑटोइम्यून सिंड्रोम तक — एक ही छत के नीचे संपूर्ण इलाज।')} />
            <Link to="/disorders-we-treat" className="btn-teal inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm">{tr('View all', 'सभी देखें')} <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-6">
            {DISORDERS.slice(0, 6).map((d, i) => (
              <Reveal key={d.id} delay={i * 70}>
                <Link to="/disorders-we-treat" className="group card-lift block bg-white rounded-2xl overflow-hidden border border-teal-50 h-full">
                  <div className="h-44 overflow-hidden">
                    <img src={IMAGES[d.image]} alt={d.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg text-teal-900 mb-2 group-hover:text-teal-700 transition-colors" style={{ fontWeight: 600 }}>{tr(d.name, d.hiName)}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-3">{tr(d.short, d.hiShort)}</p>
                    <span className="inline-flex items-center gap-1 text-teal-700 text-sm font-medium mt-4">{tr('Learn more', 'और जानें')} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DOCTOR HIGHLIGHT */}
      <section className="py-20">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="rounded-full p-2 shadow-2xl max-w-md mx-auto" style={{ background: 'linear-gradient(140deg,#ffffff,#ccf5e8)' }}>
                <img src={IMAGES.drMale} alt="Dr Puneet Srivastava" className="w-full aspect-square rounded-full object-cover" />
              </div>
              <div className="absolute -bottom-6 right-6 bg-white rounded-2xl shadow-xl p-5 max-w-[220px]">
                <div className="flex gap-1 mb-1">{[...Array(5)].map((_, i) => <Star key={i} size={15} className="fill-amber-400 text-amber-400" />)}</div>
                <p className="text-xs text-slate-600 leading-relaxed">{tr('"Meticulous clinical approach and genuine commitment to excellence."', '"बारीकी से जाँच करने का तरीका और उत्कृष्टता के प्रति सच्ची प्रतिबद्धता।"')}</p>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionTitle eyebrow={tr('Meet the Specialist', 'विशेषज्ञ से मिलें')} title={tr('Dr Puneet Srivastava', 'डॉ. पुनीत श्रीवास्तव')} />
            <p className="text-slate-600 leading-relaxed">{tr(DOCTORS[0].bio, DOCTORS[0].hiBio)}</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-7">
              {DOCTORS[0].tags.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={17} className="text-teal-600 shrink-0" /> {t}
                </div>
              ))}
            </div>
            <Link to="/who-we-are" className="btn-teal inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold mt-8">
              {tr('Read Full Profile', 'पूरी प्रोफ़ाइल पढ़ें')} <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* FIRST VISIT */}
      <section data-testid="home-first-visit-section" className="py-20">
        <div className="container-x">
          <SectionTitle eyebrow={tr('Your first visit', 'आपकी पहली मुलाक़ात')} title={tr('What to expect, step by step', 'क्या होगा, क़दम दर क़दम')} sub={tr('No rushed five-minute consultations. Here is how your first appointment works.', 'जल्दबाज़ी वाला पाँच मिनट का परामर्श नहीं। जानिए पहली मुलाक़ात कैसे होती है।')} />
          <FirstVisit />
        </div>
      </section>

      {/* ONLINE CONSULTATION */}
      <section id="online-consultation" data-testid="home-online-consult-section" className="pb-20 scroll-mt-24">
        <div className="container-x"><OnlineConsult /></div>
      </section>

      {/* SECOND OPINION CTA */}
      <section className="deep-section relative overflow-hidden">
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full blob" style={{ background: 'rgba(255,111,94,0.18)' }} />
        <div className="container-x py-16 relative z-10 grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
          <div>
            <h2 className="text-white text-3xl md:text-4xl leading-tight text-balance" style={{ fontWeight: 600 }}>{tr('Unsure about a diagnosis? Get an expert second opinion.', 'निदान को लेकर संदेह है? विशेषज्ञ से सेकंड ओपिनियन लें।')}</h2>
            <p className="text-teal-100/85 text-lg mt-4 max-w-2xl leading-relaxed">{tr('A fresh, evidence-based review of your reports and imaging can bring clarity and confidence to your treatment journey.', 'आपकी रिपोर्ट और स्कैन की नई, प्रमाण-आधारित समीक्षा आपके इलाज को स्पष्टता और भरोसा दे सकती है।')}</p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col gap-3">
            <a href={`tel:${CLINIC.phoneRaw}`} className="btn-coral inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold"><Phone size={18} /> {tr('Call', 'कॉल करें')} {CLINIC.phone}</a>
            <Link to="/second-opinion" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold bg-white/10 text-white hover:bg-white/20 transition-colors">{tr('Second Opinion', 'सेकंड ओपिनियन')} <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20">
        <div className="container-x">
          <SectionTitle center eyebrow={tr('Testimonials', 'मरीज़ों के अनुभव')} title={tr('Patients share their experience', 'मरीज़ अपना अनुभव बताते हैं')} sub={tr(`Rated ${CLINIC.rating} across ${CLINIC.reviews} Google reviews.`, `${CLINIC.reviews} गूगल रिव्यू में ${CLINIC.rating} रेटिंग।`)} />
          <div><VideoTestimonial idPrefix="home-video-testimonial" /></div>
        </div>
      </section>

      {/* CONTACT / HOURS */}
      <section className="mint-section py-20">
        <div className="container-x grid lg:grid-cols-2 gap-10">
          <div className="bg-white rounded-3xl border border-teal-50 p-8 shadow-sm">
            <SectionTitle eyebrow={tr('Visit Us', 'हमसे मिलें')} title={tr('See us in person', 'क्लिनिक आकर मिलें')} />
            <ul className="space-y-5">
              <li className="flex gap-4"><span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-700 shrink-0"><MapPin size={20} /></span><div><div className="font-semibold text-teal-900">{tr('Clinic Address', 'क्लिनिक का पता')}</div><p className="text-slate-600 text-sm mt-1">{CLINIC.address}</p></div></li>
              <li className="flex gap-4"><span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-700 shrink-0"><Phone size={20} /></span><div><div className="font-semibold text-teal-900">{tr('Appointments', 'अपॉइंटमेंट')}</div><a href={`tel:${CLINIC.phoneRaw}`} className="text-slate-600 text-sm mt-1 hover:text-teal-700">{CLINIC.phone}</a></div></li>
            </ul>
            <div className="flex gap-3 mt-7">
              <a href={CLINIC.whatsapp} target="_blank" rel="noreferrer" className="btn-coral inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm"><MessageCircle size={17} /> {tr('WhatsApp', 'व्हाट्सऐप')}</a>
              <a href={CLINIC.mapCid} target="_blank" rel="noreferrer" className="btn-outline inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm bg-white">{tr('Get Directions', 'रास्ता देखें')}</a>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-teal-50 p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6"><span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-50 text-teal-700"><Clock size={20} /></span><h3 className="font-display text-2xl text-teal-900" style={{ fontWeight: 600 }}>{tr('Clinic Hours', 'क्लिनिक का समय')}</h3></div>
            <ul className="divide-y divide-teal-50">
              {CLINIC.hours.map((h) => (
                <li key={h.d} className="flex items-center justify-between py-3">
                  <span className="text-slate-700">{tr(h.d, h.hi)}</span>
                  <span className={`text-sm font-medium ${h.t === 'Closed' ? 'text-coral' : 'text-teal-700'}`} style={h.t === 'Closed' ? { color: 'var(--coral)' } : {}}>{tr(h.t, { 'Closed': 'बंद', 'By Appointment': 'अपॉइंटमेंट से' }[h.t])}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-400 mt-4">{tr(`Please call ${CLINIC.phone} for confirmation.`, `पुष्टि के लिए कृपया ${CLINIC.phone} पर कॉल करें।`)}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
